import { defineStore } from 'pinia';
import type { EditorView } from 'codemirror';
import { Transaction } from '@codemirror/state';
import { pollUntil } from '~/utilities/poll-until';

interface State {
  editors: Map<string, EditorView>;
  activeFileId: string | null;
}

interface InsertOptions {
  /** Line number to insert at (1-based) */
  line?: number;
  /** Column number to insert at (0-based) */
  column?: number;
  /** Whether to select the inserted text */
  select?: boolean;
}

/** Represents a position in the editor */
interface Position {
  /** Absolute offset in the document */
  offset: number;
  /** Line number (1-based) */
  line: number;
  /** Column number (0-based) */
  column: number;
  /** The text content of the line */
  lineText: string;
}

/** Represents a selection range in the editor */
interface SelectionRange {
  /** Start position of the selection */
  from: Position;
  /** End position of the selection */
  to: Position;
  /** Selected text content */
  text: string;
  /** Whether the selection is empty (cursor position) */
  isEmpty: boolean;
}

/** Custom error class for editor-related errors */
class EditorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EditorError';
  }
}

export const useEditorStore = defineStore('editor', {
  state: (): State => ({
    editors: new Map(),
    activeFileId: null,
  }),

  actions: {
    /**
     * Register a new editor instance
     * @param fileId - The ID of the file
     * @param editorView - The CodeMirror editor view instance
     * @throws {EditorError} If editor is already registered
     */
    registerEditor(fileId: string, editorView: EditorView) {
      if (this.editors.has(fileId)) {
        throw new EditorError(`Editor already registered for file: ${fileId}`);
      }
      this.editors.set(fileId, editorView);

      if (!this.activeFileId) {
        this.activeFileId = fileId;
      }
    },

    /**
     * Unregister and cleanup an editor instance
     * @param fileId - The ID of the file
     */
    unregisterEditor(fileId: string) {
      const editor = this.editors.get(fileId);
      if (editor) {
        editor.destroy();
        this.editors.delete(fileId);

        if (this.activeFileId === fileId) {
          this.activeFileId = this.editors.keys().next().value || null;
        }
      }
    },

    /**
     * Clear and cleanup all editor instances
     */
    clearEditors() {
      this.editors.forEach((editor) => editor.destroy());
      this.editors.clear();
      this.activeFileId = null;
    },

    /**
     * Get an editor instance by file ID
     * @param fileId - The ID of the file
     * @returns The editor instance
     */
    async getEditor(fileId: string) {
      return pollUntil(() => this.editors.get(fileId), {
        timeoutMessage: `Editor not found for file: ${fileId}`,
        TimeoutErrorClass: EditorError,
      });
    },

    /**
     * Get detailed information about the current selection
     * @param fileId - The ID of the file
     * @returns Detailed selection information including position, text, and multi-cursor support
     * @throws {EditorError} If editor is not found
     */
    async getSelection(fileId: string) {
      const view = await this.getEditor(fileId);
      const selection = view.state.selection;
      const doc = view.state.doc;

      const getPositionDetails = (pos: number): Position => {
        const line = doc.lineAt(pos);
        return {
          offset: pos,
          line: line.number,
          column: pos - line.from,
          lineText: line.text,
        };
      };

      const getRangeDetails = (from: number, to: number): SelectionRange => ({
        from: getPositionDetails(from),
        to: getPositionDetails(to),
        text: view.state.sliceDoc(from, to),
        isEmpty: from === to,
      });

      return {
        main: getRangeDetails(selection.main.from, selection.main.to),
        ranges: selection.ranges.map((range) => getRangeDetails(range.from, range.to)),
        hasSelection: selection.ranges.some((range) => range.from !== range.to),
        rangeCount: selection.ranges.length,
        fullText: selection.ranges
          .map((range) => view.state.sliceDoc(range.from, range.to))
          .join('\n'),
      };
    },

    /**
     * Get the current cursor position(s)
     * @param fileId - The ID of the file
     * @returns Cursor position information for main and all cursors
     * @throws {EditorError} If editor is not found
     */
    async getCursorPosition(fileId: string) {
      const view = await this.getEditor(fileId);
      const selection = view.state.selection;

      return {
        main: {
          head: selection.main.head,
          anchor: selection.main.anchor,
          line: view.state.doc.lineAt(selection.main.head).number,
          column: selection.main.head - view.state.doc.lineAt(selection.main.head).from,
        },
        all: selection.ranges.map((range) => ({
          head: range.head,
          anchor: range.anchor,
          line: view.state.doc.lineAt(range.head).number,
          column: range.head - view.state.doc.lineAt(range.head).from,
        })),
      };
    },

    /**
     * Insert text at current cursor position(s)
     * @param fileId - The ID of the file
     * @param text - Text to insert
     * @param options - Insert options
     * @throws {EditorError} If editor is not found
     */
    async insertAtCursor(fileId: string, text: string, options: InsertOptions = {}) {
      const view = await this.getEditor(fileId);
      const { select = false } = options;

      const changes = view.state.selection.ranges.map((range) => ({
        from: range.from,
        to: range.to,
        insert: text,
      }));

      view.dispatch({
        changes,
        selection: select
          ? {
              anchor: view.state.selection.main.from,
              head: view.state.selection.main.from + text.length,
            }
          : undefined,
      });
    },

    /**
     * Insert text at specified line and column
     * @param fileId - The ID of the file
     * @param text - Text to insert
     * @param line - Line number (1-based)
     * @param options - Insert options
     * @throws {EditorError} If editor is not found or line is invalid
     */
    async insertAtLine(fileId: string, text: string, line: number, options: InsertOptions = {}) {
      const view = await this.getEditor(fileId);
      const { column = 0, select = false } = options;

      const doc = view.state.doc;
      const targetLine = Math.max(1, Math.min(line, doc.lines));
      const linePos = doc.line(targetLine);
      const insertPos = linePos.from + Math.min(column, linePos.length);

      view.dispatch({
        changes: {
          from: insertPos,
          insert: text,
        },
        selection: select
          ? {
              anchor: insertPos,
              head: insertPos + text.length,
            }
          : undefined,
      });
    },

    /**
     * Append text to the end of the document
     * @param fileId - The ID of the file
     * @param text - Text to append
     * @param options - Insert options
     * @throws {EditorError} If editor is not found
     */
    async appendText(fileId: string, text: string, options: InsertOptions = {}) {
      const view = await this.getEditor(fileId);
      const { select = false } = options;
      const insertPos = view.state.doc.length;

      view.dispatch({
        changes: {
          from: insertPos,
          insert: text,
        },
        selection: select
          ? {
              anchor: insertPos,
              head: insertPos + text.length,
            }
          : undefined,
      });
    },

    /**
     * Replace the current selection(s) with text
     * @param fileId - The ID of the file
     * @param text - Text to replace selection with
     * @throws {EditorError} If editor is not found
     */
    async replaceSelection(fileId: string, text: string) {
      const view = await this.getEditor(fileId);

      view.dispatch({
        changes: view.state.selection.ranges.map((range) => ({
          from: range.from,
          to: range.to,
          insert: text,
        })),
      });
    },

    /**
     * Get the content of a specific line
     * @param fileId - The ID of the file
     * @param line - Line number (1-based)
     * @returns The content of the specified line
     * @throws {EditorError} If editor is not found or line is invalid
     */
    async getLineContent(fileId: string, line: number) {
      const view = await this.getEditor(fileId);
      const doc = view.state.doc;

      if (line < 1 || line > doc.lines) {
        throw new EditorError(`Invalid line number: ${line}`);
      }

      const lineInfo = doc.line(line);
      return doc.sliceString(lineInfo.from, lineInfo.to);
    },

    /**
     * Replace the entire content of the editor
     * @param fileId - The ID of the file
     * @param content - New content to set
     * @param options - Additional options for the replacement
     * @throws {EditorError} If editor is not found
     */
    async setFileContent(
      fileId: string,
      content: string,
      options: { select?: boolean; preserveHistory?: boolean } = {},
    ) {
      const view = await this.getEditor(fileId);
      const { select = false, preserveHistory = false } = options;

      // Create the transaction
      const transaction = {
        changes: {
          from: 0,
          to: view.state.doc.length,
          insert: content,
        },
        selection: select
          ? {
              anchor: 0,
              head: content.length,
            }
          : undefined,
        // Optionally mark this as a separate history event
        annotations: preserveHistory
          ? []
          : [
              // This annotation causes the change to not be merged with other changes
              Transaction.addToHistory.of(false),
            ],
      };

      view.dispatch(transaction);
    },
  },
});
